import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { printAccessSystemVouchers } from "app/services/api/accessSystemSettings";
import { getPosWorkspacesDocument } from "app/services/api/accessSystemPosWorkspaces";
import { getLoggedInCustomerId } from "../accessSystemLoggedInCustomer";
import {
  getActiveTicketPrintersGroupedByWorkspace,
  getActiveTicketPrintersGroupedFromWorkspacesDocument,
} from "../voucherWorkspaceTicketPrinters";

const VOUCHERS_I18N = "pages.dsokDetails.accessSystem.vouchers";

/**
 * Modal: pick an active ticket printer (grouped by workspace) and POST print job.
 */
const VoucherCodesPrintModal = ({
  open,
  onClose,
  customer,
  /** @type {string[]} — voucher code strings, status ready only */
  codes,
  /** @type {string|null|undefined} — current voucher series id */
  voucherId,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const loggedInCustomerId = getLoggedInCustomerId(getAuthUser);
  const [loadingKey, setLoadingKey] = React.useState(null);
  const [workspacesDoc, setWorkspacesDoc] = React.useState(null);
  const [workspacesLoading, setWorkspacesLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setWorkspacesLoading(true);
    (async () => {
      try {
        const doc = await getPosWorkspacesDocument();
        if (!cancelled) {
          setWorkspacesDoc(doc && typeof doc === "object" ? doc : null);
        }
      } catch {
        if (!cancelled) {
          setWorkspacesDoc(null);
        }
      } finally {
        if (!cancelled) {
          setWorkspacesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const grouped = React.useMemo(() => {
    const fromApi = getActiveTicketPrintersGroupedFromWorkspacesDocument(
      workspacesDoc,
      customer?.dsokSettings?.customer?.dlaConnectionCode,
    );
    if (fromApi.length > 0) return fromApi;
    return getActiveTicketPrintersGroupedByWorkspace(customer);
  }, [customer, workspacesDoc]);

  const hasPrinters = grouped.some((g) => g.printers.length > 0);
  const isBusy = loadingKey != null || workspacesLoading;

  const handlePrint = React.useCallback(
    async (workspace, printer) => {
      const list = Array.isArray(codes) ? codes.map((c) => String(c).trim()).filter(Boolean) : [];
      if (list.length === 0) {
        enqueueSnackbar(
          t(`${VOUCHERS_I18N}.codes.print.noCodes`, {
            defaultValue: "Nie sú vybrané žiadne kódy.",
          }),
          { variant: "warning" },
        );
        return;
      }

      const key = `${workspace.workspaceKey}:${printer.deviceKey}`;
      setLoadingKey(key);
      try {
        const payload = {
          codes: list,
          customerId: loggedInCustomerId ?? undefined,
          workspaceUuid: workspace.workspaceKey,
          printerUuid: printer.uuid || printer.deviceKey,
          connectionCode: workspace.connectionCode || "",
          voucherId: voucherId != null ? String(voucherId) : undefined,
          printerName: printer.name,
          printerIp: printer.localIp || undefined,
        };
        await printAccessSystemVouchers(payload);
        enqueueSnackbar(
          t(`${VOUCHERS_I18N}.codes.print.success`, {
            defaultValue: "Tlač bola odoslaná.",
          }),
          { variant: "success" },
        );
        onClose();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t(`${VOUCHERS_I18N}.codes.print.error`, {
            defaultValue: "Tlač zlyhala.",
          });
        enqueueSnackbar(typeof msg === "string" ? msg : String(msg), {
          variant: "error",
        });
      } finally {
        setLoadingKey(null);
      }
    },
    [codes, enqueueSnackbar, loggedInCustomerId, onClose, t, voucherId],
  );

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(`${VOUCHERS_I18N}.codes.print.selectPrinterTitle`, {
          defaultValue: "Tlač voucherov — vyberte tlačiareň",
        })}
      </DialogTitle>
      <DialogContent dividers>
        {workspacesLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
          </Box>
        ) : !hasPrinters ? (
          <Alert severity="info" variant="outlined">
            {t(`${VOUCHERS_I18N}.codes.print.noPrinters`, {
              defaultValue:
                "Nie sú dostupné žiadne aktívne tlačiarne typu lístok. Skontrolujte pracoviská a zariadenia zákazníka.",
            })}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {grouped.map((ws) => (
              <Box key={ws.workspaceKey}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  {ws.workspaceName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  {ws.printers.map((printer) => {
                    const key = `${ws.workspaceKey}:${printer.deviceKey}`;
                    const loading = loadingKey === key;
                    return (
                      <Button
                        key={printer.deviceKey}
                        variant="outlined"
                        disabled={isBusy}
                        onClick={() => handlePrint(ws, printer)}
                        sx={{
                          flexDirection: "column",
                          minWidth: 100,
                          minHeight: 88,
                          py: 1.5,
                          px: 2,
                          textTransform: "none",
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={32} sx={{ mb: 0.5 }} />
                        ) : (
                          <PrintIcon
                            sx={{ fontSize: 36, mb: 0.5, color: "primary.main" }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ textAlign: "center" }}
                        >
                          {printer.name}
                        </Typography>
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          {t("buttons.cancel", { defaultValue: "Zrušiť" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoucherCodesPrintModal;
